import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Link,
  Alert,
} from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import { AccountPosting, PostingType } from '../types/account';
import {
  formatCurrency,
  formatDateTime,
  getPostingTypeColor,
  validateDoubleEntry,
} from '../utils/accountUtils';

interface PostingTableProps {
  postings: AccountPosting[];
  showAccount?: boolean;
  showTransaction?: boolean;
  showValidation?: boolean;
  dense?: boolean;
}

const PostingTable: React.FC<PostingTableProps> = ({
  postings,
  showAccount = true,
  showTransaction = true,
  showValidation = false,
  dense = false,
}) => {
  const navigate = useNavigate();

  if (postings.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <Typography color="text.secondary">No postings found</Typography>
      </Box>
    );
  }

  const validation = showValidation ? validateDoubleEntry(postings) : null;

  // Agent pool postings carry the collecting agent + voucher for reconciliation with the
  // external float system; only show the columns when at least one row has them.
  const showAgentRefs = postings.some((p) => p.agentCode || p.voucherRef);

  return (
    <>
      {showValidation && validation && (
        <Alert
          severity={validation.isBalanced ? 'success' : 'error'}
          icon={validation.isBalanced ? <CheckCircle /> : <Error />}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="body2">
              <strong>Total Debits:</strong> {formatCurrency(validation.totalDebits, postings[0]?.currency || 'USD')}
              {' | '}
              <strong>Total Credits:</strong> {formatCurrency(validation.totalCredits, postings[0]?.currency || 'USD')}
            </Typography>
            {!validation.isBalanced && (
              <Typography variant="body2" color="error">
                <strong>Difference:</strong> {formatCurrency(validation.difference, postings[0]?.currency || 'USD')}
              </Typography>
            )}
          </Box>
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size={dense ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              {showTransaction && <TableCell>Transaction Ref</TableCell>}
              {showAccount && <TableCell>Account</TableCell>}
              {showAgentRefs && <TableCell>Agent</TableCell>}
              {showAgentRefs && <TableCell>Voucher Ref</TableCell>}
              <TableCell>Description</TableCell>
              <TableCell align="right">Debit</TableCell>
              <TableCell align="right">Credit</TableCell>
              <TableCell align="right">Balance After</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {postings.map((posting) => (
              <TableRow
                key={posting.id}
                hover
                sx={{
                  '&:hover': {
                    cursor: showAccount || showTransaction ? 'pointer' : 'default',
                  },
                }}
              >
                <TableCell>
                  <Typography variant="body2">{formatDateTime(posting.postingDate)}</Typography>
                </TableCell>
                {showTransaction && (
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/transactions/${posting.transactionId}`);
                      }}
                    >
                      {posting.transactionReference || 'N/A'}
                    </Link>
                  </TableCell>
                )}
                {showAccount && (
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/accounts/${posting.accountNumber}`);
                      }}
                    >
                      {posting.accountNumber}
                    </Link>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {posting.accountName}
                    </Typography>
                  </TableCell>
                )}
                {showAgentRefs && (
                  <TableCell>
                    {posting.agentCode ? (
                      <Chip label={posting.agentCode} size="small" variant="outlined" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                )}
                {showAgentRefs && (
                  <TableCell>
                    <Typography variant="body2">{posting.voucherRef || '—'}</Typography>
                  </TableCell>
                )}
                <TableCell>
                  <Typography variant="body2">{posting.description}</Typography>
                </TableCell>
                <TableCell align="right">
                  {posting.postingType === PostingType.DEBIT && (
                    <Typography
                      variant="body2"
                      sx={{ color: 'error.main', fontWeight: 'medium' }}
                    >
                      {formatCurrency(posting.amount, posting.currency)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {posting.postingType === PostingType.CREDIT && (
                    <Typography
                      variant="body2"
                      sx={{ color: 'success.main', fontWeight: 'medium' }}
                    >
                      {formatCurrency(posting.amount, posting.currency)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="medium">
                    {formatCurrency(posting.balanceAfter, posting.currency)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default PostingTable;
